/**
 * Generative cover-art patterns (designer handoff — "Vinyl Covers").
 *
 * One pure generator per colourway, signature `(cover, seed, intensity, uid)`
 * on a 1040×1000 viewBox stretched to the jacket face (preserveAspectRatio
 * "none"). The first child is a full `base` rect; every later shape is driven
 * by the per-cover RNG and scaled in opacity by `intensity`. The generator
 * bodies are ported verbatim from the approved design source — counts, ranges
 * and opacities are not re-derived.
 *
 * `<def>` ids are suffixed with the cover key + a per-instance `uid` so the
 * six can render on one page (and repeat across slides) without collision —
 * React.useId replaces the design's module counter (SSR-safe under Inertia).
 */

import type { CSSProperties, ReactNode } from "react"
import { useId } from "react"

import type { Cover } from "./cover-art"
import { COVER_INTENSITY, rngForSeed, rr } from "./cover-art"

const VB = "0 0 1040 1000"
const svgStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  display: "block",
}

type Generator = (c: Cover, seed: string, k: number, uid: string) => ReactNode

const artWrap = (c: Cover, defs: ReactNode, kids: ReactNode): ReactNode => (
  <svg
    viewBox={VB}
    preserveAspectRatio="none"
    style={svgStyle}
    aria-hidden="true"
  >
    {defs ? <defs>{defs}</defs> : null}
    <rect x={0} y={0} width={1040} height={1000} fill={c.base} />
    {kids}
  </svg>
)

/** petrol — orbits & starfield. */
const petrolArt: Generator = (c, sd, k) => {
  const rng = rngForSeed(sd, c.key)
  const kids: ReactNode[] = []
  const cx = rr(rng, 880, 980)
  const cy = rr(rng, 420, 560)
  const n = 5 + Math.floor(rng() * 3)
  for (let i = 0; i < n; i++) {
    const rx = 150 + i * 128 + rr(rng, -18, 18)
    const ry = rx * rr(rng, 0.72, 0.9)
    kids.push(
      <ellipse
        key={`o${i}`}
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        fill="none"
        stroke={c.ink}
        strokeWidth={1.8}
        strokeOpacity={0.55 * k}
        transform={`rotate(${rr(rng, -28, 28).toFixed(1)} ${cx} ${cy})`}
      />,
    )
  }
  for (let i = 0; i < 3; i++)
    kids.push(
      <circle
        key={`p${i}`}
        cx={rr(rng, 120, 1000)}
        cy={rr(rng, 140, 880)}
        r={rr(rng, 7, 18)}
        fill={c.ink}
        fillOpacity={0.6 * k}
      />,
    )
  for (let i = 0; i < 70; i++)
    kids.push(
      <circle
        key={`s${i}`}
        cx={rr(rng, 0, 1040)}
        cy={rr(rng, 0, 1000)}
        r={rr(rng, 0.8, 2.6)}
        fill={c.glow}
        fillOpacity={rr(rng, 0.12, 0.55) * k}
      />,
    )
  return artWrap(c, null, kids)
}

/** gold — rising sun. */
const goldArt: Generator = (c, sd, k) => {
  const rng = rngForSeed(sd, c.key)
  const kids: ReactNode[] = []
  const cx = rr(rng, 950, 1045)
  const cy = rr(rng, 430, 570)
  const rays = 26 + Math.floor(rng() * 8)
  for (let i = 0; i < rays; i++) {
    const a = (i / rays) * Math.PI * 2 + rng() * 0.06
    kids.push(
      <line
        key={`r${i}`}
        x1={cx}
        y1={cy}
        x2={cx + Math.cos(a) * 1500}
        y2={cy + Math.sin(a) * 1500}
        stroke={c.ink}
        strokeWidth={rr(rng, 6, 16)}
        strokeOpacity={0.13 * k}
      />,
    )
  }
  const n = 6 + Math.floor(rng() * 3)
  for (let i = 0; i < n; i++)
    kids.push(
      <circle
        key={`c${i}`}
        cx={cx}
        cy={cy}
        r={95 + i * 92}
        fill="none"
        stroke={c.ink}
        strokeWidth={2}
        strokeOpacity={0.4 * k}
      />,
    )
  kids.push(
    <circle
      key="core"
      cx={cx}
      cy={cy}
      r={rr(rng, 72, 110)}
      fill={c.ink}
      fillOpacity={0.5 * k}
    />,
  )
  return artWrap(c, null, kids)
}

/** jade — colour wheel fanning in from the right. */
const jadeArt: Generator = (c, sd, k) => {
  const rng = rngForSeed(sd, c.key)
  const kids: ReactNode[] = []
  const cx = rr(rng, 960, 1045)
  const cy = 500
  const R = 600
  const seg = 28 + Math.floor(rng() * 6) * 2
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2
    const a1 = ((i + 1) / seg) * Math.PI * 2
    const x0 = cx + Math.cos(a0) * R
    const y0 = cy + Math.sin(a0) * R
    const x1 = cx + Math.cos(a1) * R
    const y1 = cy + Math.sin(a1) * R
    const d = `M ${cx} ${cy} L ${x0.toFixed(1)} ${y0.toFixed(1)} A ${R} ${R} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)} Z`
    kids.push(
      <path
        key={i}
        d={d}
        fill={c.ink}
        fillOpacity={(i % 2 ? 0.34 : 0.1) * k}
      />,
    )
  }
  for (let i = 1; i <= 3; i++)
    kids.push(
      <circle
        key={`ring${i}`}
        cx={cx}
        cy={cy}
        r={(R * i) / 3.3}
        fill="none"
        stroke={c.base}
        strokeWidth={4}
        strokeOpacity={0.55}
      />,
    )
  return artWrap(c, null, kids)
}

/** peri — Saville coded colour grid. */
const periArt: Generator = (c, sd, k) => {
  const rng = rngForSeed(sd, c.key)
  const kids: ReactNode[] = []
  const cell = 64
  for (let gx = 0; gx < 1040; gx += cell)
    for (let gy = 0; gy < 1000; gy += cell) {
      const r = rng()
      if (r < 0.28)
        kids.push(
          <rect
            key={`${gx}_${gy}`}
            x={gx + 8}
            y={gy + 8}
            width={cell - 16}
            height={cell - 16}
            fill={c.ink}
            fillOpacity={0.34 * k}
          />,
        )
      else if (r < 0.5)
        kids.push(
          <rect
            key={`${gx}_${gy}`}
            x={gx + 8}
            y={gy + 8}
            width={cell - 16}
            height={cell - 16}
            fill="none"
            stroke={c.ink}
            strokeWidth={2}
            strokeOpacity={0.42 * k}
          />,
        )
    }
  return artWrap(c, null, kids)
}

/** rose — painterly bloom. */
const roseArt: Generator = (c, sd, k, uid) => {
  const rng = rngForSeed(sd, c.key)
  const gid = `rg_${c.key}_${uid}`
  const defs = (
    <radialGradient id={gid}>
      <stop offset="0%" stopColor={c.ink} stopOpacity={0.5 * k} />
      <stop offset="100%" stopColor={c.ink} stopOpacity={0} />
    </radialGradient>
  )
  const kids: ReactNode[] = []
  const cx = rr(rng, 790, 950)
  const cy = rr(rng, 520, 720)
  for (let i = 0; i < 11; i++) {
    const ang = rng() * Math.PI * 2
    const dist = rr(rng, 0, 270)
    kids.push(
      <circle
        key={`b${i}`}
        cx={cx + Math.cos(ang) * dist}
        cy={cy + Math.sin(ang) * dist}
        r={rr(rng, 120, 250)}
        fill={`url(#${gid})`}
      />,
    )
  }
  for (let i = 0; i < 22; i++)
    kids.push(
      <circle
        key={`d${i}`}
        cx={rr(rng, 0, 1040)}
        cy={rr(rng, 0, 1000)}
        r={rr(rng, 20, 70)}
        fill={`url(#${gid})`}
        opacity={0.55}
      />,
    )
  return artWrap(c, defs, kids)
}

/** coral — luminous fluid bands (the Crush scan-lines). */
const coralArt: Generator = (c, sd, k, uid) => {
  const rng = rngForSeed(sd, c.key)
  const gid = `cg_${c.key}_${uid}`
  const defs = (
    <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor={c.ink} stopOpacity={0} />
      <stop offset="42%" stopColor={c.ink} stopOpacity={0.5 * k} />
      <stop offset="52%" stopColor={c.glow} stopOpacity={0.6 * k} />
      <stop offset="100%" stopColor={c.ink} stopOpacity={0} />
    </linearGradient>
  )
  const kids: ReactNode[] = []
  const bands = 7 + Math.floor(rng() * 3)
  for (let b = 0; b < bands; b++) {
    const baseY = rr(rng, 30, 970)
    const amp = rr(rng, 30, 95)
    const per = rr(rng, 230, 430)
    const ph = rng() * 6.28
    const wt = rr(rng, 10, 34)
    let d = `M -20 ${baseY.toFixed(1)}`
    for (let x = -20; x <= 1060; x += 18) {
      const y = baseY + amp * Math.sin((x / per) * 6.28 + ph)
      d += ` L ${x} ${y.toFixed(1)}`
    }
    kids.push(
      <path
        key={b}
        d={d}
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth={wt}
        strokeOpacity={0.9}
        strokeLinecap="round"
      />,
    )
  }
  return artWrap(c, defs, kids)
}

const ARTFN: Record<string, Generator> = {
  petrol: petrolArt,
  jade: jadeArt,
  peri: periArt,
  rose: roseArt,
  coral: coralArt,
  gold: goldArt,
}

/**
 * The seeded generative print for one sleeve, stretched to fill the jacket
 * face. `intensity` scales every shape's opacity (design default 0.65).
 */
export function PatternField({
  cover,
  seed,
  intensity = COVER_INTENSITY,
}: {
  cover: Cover
  seed: string
  intensity?: number
}) {
  const uid = useId().replace(/:/g, "")
  const draw = ARTFN[cover.key] ?? petrolArt
  return draw(cover, seed, intensity, uid)
}
